import { selectNumCaseUploads } from "@/store/slices/casesSlice";
import { RootState } from "@/store/store";
import { Box, Theme } from "@mui/material";
import { Dispatch, SetStateAction, useState } from "react";
import { useDragLayer } from "react-dnd";
import toast from "react-hot-toast";
import { useSelector } from "react-redux";

interface DragAndDropUploadProps {
  children: React.ReactNode;
  // Callback function to pass files back to the parent.
  onFilesAdded: (cases: DataTransferItem[]) => void;
  onDragOver?: boolean;
  setOnDragOver?: Dispatch<SetStateAction<boolean>>;
}

export const DragAndDropUpload: React.FC<DragAndDropUploadProps> = ({ children, onFilesAdded, onDragOver, setOnDragOver }) => {
  const DragAlert = "dragAlertId";
  const [newOnDragOver, setNewOnDragOver] = useState<boolean>(false);
  const { areScansBeingDragged } = useDragLayer((monitor) => ({
    areScansBeingDragged: monitor.isDragging() && monitor.getItemType() === "scan"
  }));

  if (onDragOver === undefined || setOnDragOver === undefined) {
    onDragOver = newOnDragOver;
    setOnDragOver = setNewOnDragOver;
  }

  const numCases = useSelector((state: RootState) => selectNumCaseUploads(state));

  const onDrop = async (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    toast.remove(DragAlert);
    setOnDragOver(false);

    // This check prevents accidental triggers from dragging and dropping content from the page.
    // Folders and files will return "" from getData("text/plain"). Content on the page will be converted to text.
    if (!areScansBeingDragged && e.dataTransfer.getData("text/plain") === "") {
      const items = Array.from(e.dataTransfer.items);
      onFilesAdded(items);
    }

    // Reset input if applicable (ensure e.target is an input element)
    (e.target as HTMLInputElement).value = "";
  };

  const handleOnDragFileOver = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    if (areScansBeingDragged) {
      return;
    }

    setOnDragOver(true);
  };

  const handleOnDragFileExit = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    const isMouseInside = e.currentTarget.contains(e.relatedTarget as Node);
    if (isMouseInside) {
      return;
    }
    toast.remove(DragAlert);
    setOnDragOver(false);
  };

  return (
    <Box
      onDragOver={(e: React.DragEvent<HTMLDivElement>) => handleOnDragFileOver(e)}
      onDragLeave={(e: React.DragEvent<HTMLDivElement>) => handleOnDragFileExit(e)}
      onDrop={(e: React.DragEvent<HTMLDivElement>) => onDrop(e)}
      sx={{
        width: "100%",
        height: "100%",
        boxSizing: "border-box",
        border: numCases > 0 ? "none" : (theme: Theme) => `2px dashed ${theme.outline.main}`,
        borderRadius: "8px",
        ...(onDragOver && {
          border: "2px dashed black",
          borderRadius: "8px",
          "& > *": {
            pointerEvents: "none"
          }
        })
      }}
      component="div"
    >
      {children}
    </Box>
  );
};
